import type {
  GoldenTriangle,
  CostMetric,
  TimeMetric,
  QualityMetric,
  TriangleAlert,
  TriangleBalance,
  TriangleConfig,
  MetricStatus,
} from '../types/triangle';
import { DEFAULT_TRIANGLE_CONFIG } from '../types/triangle';
import type { PertNode, PertEdge, Project, Resource } from '../types';
import { formatCurrency, getLocaleFromTimezone } from './currency';

// ============================================================================
// TRIANGLE D'OR CALCULATOR
// ============================================================================

/**
 * Calculate the cost metric based on project resources and budget
 */
export function calculateCostMetric(
  nodes: PertNode[],
  _project: Project,
  config: TriangleConfig
): CostMetric {
  // Calculate total budget from resources
  let budgetTotal = 0;
  let budgetUsed = 0;

  nodes.forEach((node) => {
    if (node.resources && node.resources.length > 0) {
      node.resources.forEach((resource: Resource) => {
        const cost = resource.cost || 0;
        budgetTotal += cost;

        // Calculate used budget based on task progress
        const progress = (node.progress || 0) / 100;
        budgetUsed += cost * progress;
      });
    }
  });

  // If no resources defined, use a default budget calculation
  if (budgetTotal === 0) {
    budgetTotal = 100000; // Default budget
    budgetUsed = (budgetTotal * getAverageProgress(nodes)) / 100;
  }

  const budgetRemaining = budgetTotal - budgetUsed;
  const value = budgetTotal > 0 ? (budgetUsed / budgetTotal) * 100 : 0;
  const variance = budgetRemaining;

  // Determine status
  let status: MetricStatus = 'good';
  if (value >= config.costCriticalThreshold) {
    status = 'critical';
  } else if (value >= config.costWarningThreshold) {
    status = 'warning';
  }

  return {
    value,
    budgetTotal,
    budgetUsed,
    budgetRemaining,
    variance,
    status,
  };
}

/**
 * Calculate the time metric based on project schedule
 */
export function calculateTimeMetric(
  nodes: PertNode[],
  project: Project,
  config: TriangleConfig
): TimeMetric {
  const plannedStartDate = project.startDate ? new Date(project.startDate) : null;
  const currentDate = new Date();

  // Calculate total duration from PERT
  const totalDuration = nodes.length > 0 ? Math.max(...nodes.map((n) => n.earlyFinish || 0)) : 0;

  // Calculate elapsed time
  let elapsedDuration = 0;
  if (plannedStartDate) {
    const elapsed = currentDate.getTime() - plannedStartDate.getTime();
    elapsedDuration = Math.max(0, Math.ceil(elapsed / (1000 * 60 * 60 * 24)));
  }

  const remainingDuration = Math.max(0, totalDuration - elapsedDuration);

  // Calculate projected end date
  let projectedEndDate: Date | null = null;
  if (plannedStartDate && totalDuration > 0) {
    projectedEndDate = new Date(plannedStartDate);
    projectedEndDate.setDate(projectedEndDate.getDate() + totalDuration);
  }

  // Calculate planned end date (from project or calculated)
  const plannedEndDate = project.endDate
    ? new Date(project.endDate)
    : projectedEndDate;

  // Calculate time value (% of time elapsed)
  const value = totalDuration > 0 ? (elapsedDuration / totalDuration) * 100 : 0;

  // Calculate schedule variance
  const averageProgress = getAverageProgress(nodes);
  const variance = averageProgress - value; // Positive = ahead of schedule

  // Determine status
  let status: MetricStatus = 'good';
  const progressVsTime = averageProgress - value;

  if (progressVsTime < -config.timeCriticalThreshold) {
    status = 'critical'; // Far behind schedule
  } else if (progressVsTime < -config.timeWarningThreshold) {
    status = 'warning'; // Behind schedule
  }

  return {
    value,
    totalDuration,
    elapsedDuration,
    remainingDuration,
    variance,
    status,
    plannedStartDate,
    plannedEndDate,
    currentDate,
    projectedEndDate,
  };
}

/**
 * Calculate the quality metric based on task completion and critical path
 */
export function calculateQualityMetric(
  nodes: PertNode[],
  _edges: PertEdge[],
  config: TriangleConfig
): QualityMetric {
  const totalTasks = nodes.length;
  const completedTasks = nodes.filter((n) => (n.progress || 0) === 100).length;
  const criticalTasks = nodes.filter((n) => n.isCritical).length;
  const criticalTasksCompleted = nodes.filter(
    (n) => n.isCritical && (n.progress || 0) === 100
  ).length;

  // Calculate defects (tasks with delays or issues)
  // For now, we consider tasks with zero slack as potential issues
  const defects = nodes.filter((n) => n.slack === 0 && (n.progress || 0) < 100).length;

  // Calculate quality score
  const completionRate = totalTasks > 0 ? (completedTasks / totalTasks) * 100 : 0;
  const criticalCompletionRate =
    criticalTasks > 0 ? (criticalTasksCompleted / criticalTasks) * 100 : 100;
  const defectRate = totalTasks > 0 ? (defects / totalTasks) * 100 : 0;

  // Weighted quality score
  const value = completionRate * 0.5 + criticalCompletionRate * 0.4 - defectRate * 0.1;

  // Determine status
  let status: MetricStatus = 'good';
  if (value < config.qualityCriticalThreshold) {
    status = 'critical';
  } else if (value < config.qualityWarningThreshold) {
    status = 'warning';
  }

  return {
    value: Math.max(0, Math.min(100, value)),
    completedTasks,
    totalTasks,
    criticalTasks,
    criticalTasksCompleted,
    defects,
    status,
  };
}

/**
 * Determine the project balance type
 */
export function determineBalance(
  cost: CostMetric,
  time: TimeMetric,
  quality: QualityMetric
): TriangleBalance {
  const costScore = 100 - cost.value; // Lower is better for cost
  const timeScore = 100 - time.value; // Lower is better for time
  const qualityScore = quality.value; // Higher is better for quality

  const scores = [
    { type: 'cost' as const, score: costScore },
    { type: 'time' as const, score: timeScore },
    { type: 'quality' as const, score: qualityScore },
  ];

  // Sort by score descending
  scores.sort((a, b) => b.score - a.score);

  const maxScore = scores[0].score;
  const minScore = scores[2].score;
  const scoreDifference = maxScore - minScore;

  // If scores are balanced (within 15 points)
  if (scoreDifference < 15) {
    return 'optimal';
  }

  // Otherwise, identify the focus
  const focus = scores[0].type;
  return `${focus}-focused` as TriangleBalance;
}

/**
 * Generate alerts based on metrics
 */
export function generateAlerts(
  cost: CostMetric,
  time: TimeMetric,
  quality: QualityMetric,
  formatBudgetValue: (value: number) => string
): TriangleAlert[] {
  const alerts: TriangleAlert[] = [];
  const timestamp = new Date();

  // Cost alerts
  if (cost.status === 'critical') {
    alerts.push({
      id: `cost-critical-${Date.now()}`,
      type: 'critical',
      dimension: 'cost',
      message: `Budget critique : ${cost.value.toFixed(1)}% consommé (${formatBudgetValue(cost.budgetUsed)} / ${formatBudgetValue(cost.budgetTotal)})`,
      recommendation: 'Réduire le périmètre ou augmenter le budget',
      timestamp,
    });
  } else if (cost.status === 'warning') {
    alerts.push({
      id: `cost-warning-${Date.now()}`,
      type: 'warning',
      dimension: 'cost',
      message: `Attention au budget : ${cost.value.toFixed(1)}% consommé`,
      recommendation: 'Surveiller les dépenses à venir',
      timestamp,
    });
  }

  // Time alerts
  if (time.status === 'critical') {
    const delay = Math.abs(time.variance);
    alerts.push({
      id: `time-critical-${Date.now()}`,
      type: 'critical',
      dimension: 'time',
      message: `Retard critique : ${delay.toFixed(1)} jours de retard`,
      recommendation: 'Augmenter les ressources ou réduire le scope',
      timestamp,
    });
  } else if (time.status === 'warning') {
    alerts.push({
      id: `time-warning-${Date.now()}`,
      type: 'warning',
      dimension: 'time',
      message: 'Projet en retard par rapport au planning',
      recommendation: 'Accélérer les tâches critiques',
      timestamp,
    });
  }

  // Quality alerts
  if (quality.status === 'critical') {
    alerts.push({
      id: `quality-critical-${Date.now()}`,
      type: 'critical',
      dimension: 'quality',
      message: `Qualité critique : score de ${quality.value.toFixed(1)}%`,
      recommendation: 'Revoir les tâches incomplètes et résoudre les problèmes',
      timestamp,
    });
  } else if (quality.status === 'warning') {
    alerts.push({
      id: `quality-warning-${Date.now()}`,
      type: 'warning',
      dimension: 'quality',
      message: `Qualité en baisse : ${quality.defects} problèmes détectés`,
      recommendation: 'Focus sur les tâches critiques',
      timestamp,
    });
  }

  return alerts;
}

/**
 * Generate recommendations based on triangle state
 */
export function generateRecommendations(
  cost: CostMetric,
  time: TimeMetric,
  quality: QualityMetric,
  balance: TriangleBalance,
  formatBudgetValue: (value: number) => string
): string[] {
  const recommendations: string[] = [];

  switch (balance) {
    case 'cost-focused':
      recommendations.push('Projet orienté budget : Surveiller le temps et la qualité');
      if (time.status !== 'good') {
        recommendations.push('Envisager d\'augmenter le budget pour respecter les délais');
      }
      break;

    case 'time-focused':
      recommendations.push('Projet orienté délais : Surveiller le budget et la qualité');
      if (cost.status !== 'good') {
        recommendations.push('Attention aux coûts : risque de dépassement budgétaire');
      }
      break;

    case 'quality-focused':
      recommendations.push('Projet orienté qualité : Surveiller le budget et les délais');
      if (time.status !== 'good' || cost.status !== 'good') {
        recommendations.push('Compromis nécessaire entre qualité et contraintes temps/budget');
      }
      break;

    case 'optimal':
      recommendations.push('Triangle équilibré : Maintenir cet équilibre');
      break;
  }

  // Specific recommendations
  if (cost.variance < 0) {
    recommendations.push(`Dépassement budgétaire de ${formatBudgetValue(Math.abs(cost.variance))}`);
  }

  if (time.variance < -10) {
    recommendations.push('Retard significatif : Prioriser les tâches critiques');
  }

  if (quality.defects > 0) {
    recommendations.push(`${quality.defects} tâches critiques nécessitent une attention`);
  }

  return recommendations;
}

/**
 * Calculate overall health score
 */
export function calculateOverallHealth(
  cost: CostMetric,
  time: TimeMetric,
  quality: QualityMetric,
  config: TriangleConfig
): number {
  const costHealth = 100 - cost.value; // Inverted: lower cost usage is better
  const timeHealth = 100 - Math.abs(time.variance); // Lower variance is better
  const qualityHealth = quality.value;

  const overallHealth =
    costHealth * config.costWeight +
    timeHealth * config.timeWeight +
    qualityHealth * config.qualityWeight;

  return Math.max(0, Math.min(100, overallHealth));
}

/**
 * Main function to calculate the complete Golden Triangle
 */
export function calculateGoldenTriangle(
  nodes: PertNode[],
  edges: PertEdge[],
  project: Project,
  config: TriangleConfig = DEFAULT_TRIANGLE_CONFIG
): GoldenTriangle {
  const currencyCode = project.settings?.currency || 'EUR';
  const timezone = project.settings?.timezone || 'Europe/Paris';
  const currencyLocale = getLocaleFromTimezone(timezone);
  const formatBudgetValue = (value: number) => formatCurrency(value, currencyCode, { locale: currencyLocale });

  const cost = calculateCostMetric(nodes, project, config);
  const time = calculateTimeMetric(nodes, project, config);
  const quality = calculateQualityMetric(nodes, edges, config);

  const balance = determineBalance(cost, time, quality);
  const alerts = generateAlerts(cost, time, quality, formatBudgetValue);
  const recommendations = generateRecommendations(cost, time, quality, balance, formatBudgetValue);
  const overallHealth = calculateOverallHealth(cost, time, quality, config);

  return {
    id: `triangle-${project.id}`,
    projectId: project.id,
    cost,
    time,
    quality,
    balance,
    overallHealth,
    alerts,
    recommendations,
    lastCalculated: new Date(),
    autoUpdate: true,
  };
}

/**
 * Helper: Calculate average progress across all nodes
 */
function getAverageProgress(nodes: PertNode[]): number {
  if (nodes.length === 0) return 0;
  const totalProgress = nodes.reduce((sum, node) => sum + (node.progress || 0), 0);
  return totalProgress / nodes.length;
}
